import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { useNavigate } from "react-router-dom";
import api, { getCurrentUserId } from "../../services/api";
import "./Agents.css";

const FeedbackAgent = () => {
  const [submissions, setSubmissions] = useState([]);
  const [feedback, setFeedback] = useState("");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get current user ID from JWT token
        const currentUserId = getCurrentUserId();

        // Check if user is authenticated
        if (!currentUserId) {
          console.warn("No authenticated user found, redirecting to login");
          navigate("/login");
          return;
        }

        const userData = JSON.parse(localStorage.getItem("user") || "{}");
        const studentId =
          currentUserId ||
          userData.student_id ||
          userData.user_id ||
          getCurrentUserId();

        console.log("Fetching submissions for student:", studentId);
        console.log("Current authenticated user ID:", currentUserId);

        const response = await api.post("/api/agents/feedback/analyze", {
          student_id: studentId,
          action: "get_submissions",
        });

        if (response.data && response.data.success === false) {
          throw new Error(
            response.data.message || "Failed to load submissions"
          );
        }

        // Handle response data
        if (response.data.success) {
          setSubmissions(response.data.submissions || []);
        } else if (Array.isArray(response.data)) {
          setSubmissions(response.data);
        } else {
          setSubmissions([]);
        }
        setLoading(false);
      } catch (error) {
        console.error("Submissions error:", error);
        setError(
          error.message || "Failed to load submissions. Please try again later."
        );
        setLoading(false);

        // Set fallback data if the API fails
        setSubmissions([
          {
            id: "sub1",
            title: "Biology Assignment 1",
            type: "Assignment",
            submittedAt: new Date().toISOString(),
            status: "Graded",
          },
          {
            id: "sub2",
            title: "Mathematics Quiz 3",
            type: "Quiz",
            submittedAt: new Date().toISOString(),
            status: "Pending",
          },
        ]);
      }
    };

    fetchSubmissions();
  }, [refreshKey]);

  const requestFeedback = async (submissionId) => {
    setFeedbackLoading(true);
    setFeedbackError(null);
    setFeedback("");

    try {
      // Get current user ID from JWT token
      const currentUserId = getCurrentUserId();
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
      const studentId =
        currentUserId ||
        userData.student_id ||
        userData.user_id ||
        getCurrentUserId();

      const response = await api.post("/api/agents/feedback/analyze", {
        student_id: studentId,
        quiz_id: submissionId,
        action: "student_feedback",
      });

      if (response.data && response.data.success === false) {
        throw new Error(response.data.message || "Failed to get feedback");
      }

      // Handle different response formats
      let feedbackData;
      if (response.data.student_feedback_result) {
        feedbackData = response.data.student_feedback_result[0];
      } else if (response.data.feedback) {
        feedbackData = response.data.feedback;
      } else {
        feedbackData = response.data;
      }

      setFeedback(feedbackData);
      setFeedbackLoading(false);
    } catch (error) {
      console.error("Feedback error:", error);
      setFeedbackError(
        error.message || "Failed to get feedback. Please try again."
      );
      setFeedbackLoading(false);

      // Set fallback feedback data if the API fails
      setFeedback({
        message:
          "Great effort on this submission! Your understanding of core concepts is evident. Focus on providing more detailed explanations and supporting your arguments with additional references. Consider reviewing chapter 4 for more practical examples to strengthen your work.",
        strengths: [
          "Excellent understanding of core concepts",
          "Well-structured responses with clear explanations",
          "Good use of relevant examples",
        ],
        improvements: [
          "Consider adding more detailed explanations",
          "Include more references to support your arguments",
          "Work on improving the formatting of diagrams",
        ],
        suggestions:
          "Focus on connecting theoretical concepts with practical applications in your next submission. Review chapter 4 of your textbook for more examples.",
      });
    }
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    setFeedback("");
    setSelectedSubmission(null);
  };

  if (loading)
    return <div className="agent-loading">Loading submissions...</div>;

  return (
    <div className="agent-container">
      <h1 className="agent-title">Learning Insights</h1>
      <p
        className="agent-subtitle"
        style={{
          color: "#666",
          marginTop: "-10px",
          marginBottom: "20px",
          fontSize: "16px",
        }}
      >
        View your quiz history and AI-powered feedback to track your learning
        progress
      </p>

      {error && (
        <div className="error-message" style={{ marginBottom: "15px" }}>
          <p>{error}</p>
          <button onClick={handleRefresh} className="refresh-button">
            Try Again
          </button>
        </div>
      )}

      <div className="feedback-header">
        <h2>Your Quiz History</h2>
        <button
          onClick={handleRefresh}
          className="refresh-button"
          title="Refresh submissions"
        >
          <span role="img" aria-label="refresh">
            🔄
          </span>{" "}
          Refresh
        </button>
      </div>

      <div className="feedback-content">
        <section className="submissions-section">
          {submissions.length === 0 ? (
            <div className="no-data-message">
              <p>
                📚 No quiz attempts yet! Complete a quiz to see your results and
                AI-powered feedback here.
              </p>
              <button
                onClick={() => navigate("/agents/quiz")}
                style={{
                  marginTop: "15px",
                  padding: "10px 20px",
                  backgroundColor: "#4a90e2",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "16px",
                }}
              >
                Take a Quiz Now
              </button>
            </div>
          ) : (
            <div className="submissions-list">
              {submissions.map((submission, index) => (
                <div
                  key={index}
                  className={`submission-card ${
                    selectedSubmission === submission.id ? "selected" : ""
                  }`}
                  onClick={() => {
                    setSelectedSubmission(submission.id);
                    requestFeedback(submission.id);
                  }}
                >
                  <h3>{submission.title}</h3>
                  <p className="submission-type">{submission.type}</p>
                  <p className="submission-date">
                    Submitted:{" "}
                    {new Date(submission.submittedAt).toLocaleDateString()}
                  </p>
                  <div
                    className="submission-status"
                    data-status={submission.status}
                  >
                    {submission.status}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {feedbackLoading ? (
          <section className="feedback-section">
            <h2>Loading Feedback...</h2>
            <div className="feedback-loading">
              <p>Retrieving personalized feedback...</p>
            </div>
          </section>
        ) : feedbackError ? (
          <section className="feedback-section">
            <h2>Feedback Error</h2>
            <div className="feedback-error">
              <p>{feedbackError}</p>
              <button
                onClick={() => requestFeedback(selectedSubmission)}
                className="retry-button"
              >
                Retry
              </button>
            </div>
          </section>
        ) : (
          feedback && (
            <section className="feedback-section">
              <h2>Feedback</h2>
              <div className="feedback-details">
                {feedback.message && (
                  <div className="feedback-message">
                    <p>{feedback.message}</p>
                  </div>
                )}

                {feedback.strengths && feedback.strengths.length > 0 && (
                  <div className="feedback-points">
                    <h3>Strengths</h3>
                    <ul>
                      {feedback.strengths.map((point, index) => (
                        <li key={index}>{point}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {feedback.improvements && feedback.improvements.length > 0 && (
                  <div className="feedback-points">
                    <h3>Areas for Improvement</h3>
                    <ul>
                      {feedback.improvements.map((point, index) => (
                        <li key={index}>{point}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {feedback.suggestions && (
                  <div className="feedback-suggestions">
                    <h3>Suggestions</h3>
                    <p>{feedback.suggestions}</p>
                  </div>
                )}
              </div>
            </section>
          )
        )}
      </div>

      <div className="agent-footer">
        <button className="back-button" onClick={() => navigate("/dashboard")}>
          Back to Dashboard
        </button>

        <div className="feedback-info">
          <p>
            <strong>How it works:</strong> The Feedback Agent provides
            personalized feedback on your submissions to help you improve your
            understanding and performance. Select a submission from the list to
            view detailed feedback from your instructors and AI-assisted
            analysis.
          </p>
        </div>
      </div>
    </div>
  );
};

// Wrap in the shared layout so the page has the site header and footer
const FeedbackAgentPage = (props) => (
  <Layout>
    <FeedbackAgent {...props} />
  </Layout>
);

export default FeedbackAgentPage;
